/**
 * Admin web API client (ODE-P01-T10).
 *
 * Thin typed client over the @ode/api admin surfaces:
 *   POST /v1/admin/auth/login          staff login → opaque session token
 *   GET  /v1/admin/dashboard/<module>  guarded placeholder module (REQ-043..048)
 *
 * T04 envelope handling: non-2xx responses are parsed into
 * `{ success:false, error:{code,message}, requestId, timestamp }` and mapped
 * to typed outcomes. The client never logs or persists tokens — the caller
 * (AuthContext) keeps the raw token in memory only.
 *
 * Traces to: ODE-PRD-REQ-043..048, ODE-PRD-REQ-077, ODE-PRD-REQ-090.
 */

export interface ApiErrorEnvelope {
  readonly success: false;
  readonly error: { readonly code: string; readonly message: string };
  readonly requestId: string;
  readonly timestamp: string;
}

/** Typed API failure (T04 envelope). */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export interface StaffInfo {
  readonly staffId: string;
  readonly displayName: string | null;
  readonly roles: readonly string[];
}

export interface LoginResult {
  readonly token: string;
  readonly tokenType: string;
  readonly staff: StaffInfo;
  readonly session: {
    readonly sessionId: string;
    readonly issuedAt: string;
    readonly expiresAt: string;
  };
}

/** Outcome of a guarded module fetch (status + parsed payload/message). */
export interface ModuleFetchResult {
  readonly status: number;
  readonly payload: unknown;
  readonly message: string | null;
}

/** One live driver position row (REQ-043) — mirror of the server contract. */
export interface OverviewLivePosition {
  readonly driverId: string;
  readonly deliveryOrderId: string;
  readonly trackingSessionId: string;
  readonly state: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters: number | null;
  readonly capturedAtEpochMs: number;
  readonly freshnessMs: number;
}

/** One Delivery Order status row (REQ-044). */
export interface OverviewDoStatusRow {
  readonly deliveryOrderId: string;
  readonly status: string;
  readonly hasActiveSession: boolean;
  readonly lastActivityAtEpochMs: number | null;
}

/** KPI summary (REQ-043/044/048 aggregation). */
export interface OverviewKpi {
  readonly activeSessions: number;
  readonly activeDrivers: number;
  readonly totalSamples: number;
  readonly openGaps: number;
  readonly stalePositions: number;
  readonly doStatusCounts: Readonly<Record<string, number>>;
}

/** Compact operational recap (REQ-048). */
export interface OverviewRecap {
  readonly totalDeliveryOrders: number;
  readonly inFlightDeliveryOrders: number;
  readonly completedDeliveryOrders: number;
  readonly cancelledDeliveryOrders: number;
  readonly activeSessions: number;
  readonly activeDrivers: number;
  readonly totalSamples: number;
  readonly openGaps: number;
  readonly stalePositions: number;
  readonly lastFixAtEpochMs: number | null;
  readonly oldestOpenGapAtEpochMs: number | null;
}

/** Server-derived overview snapshot (REQ-043/044/048) — never client-computed. */
export interface OverviewSnapshot {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly staleAfterMs: number;
  readonly kpi: OverviewKpi;
  readonly livePositions: readonly OverviewLivePosition[];
  readonly doStatus: readonly OverviewDoStatusRow[];
  readonly recap: OverviewRecap;
}

/** Outcome of an overview snapshot fetch (status + payload/message). */
export interface OverviewFetchResult {
  readonly status: number;
  readonly payload: OverviewSnapshot | null;
  readonly message: string | null;
}

/** One admin DO search-result row (REQ-044 status + REQ-074 completion state). */
export interface AdminDoSearchRow {
  readonly deliveryOrderId: string;
  readonly doNumber: string;
  /** REQ-044 server-authoritative DO lifecycle status. */
  readonly status: string;
  /** REQ-074 completion state (never collapsed). */
  readonly completionState: string;
  readonly completionAuthoritative: boolean;
  readonly driverRef: string | null;
  readonly vehicleRef: string | null;
  readonly customerRef: string | null;
  readonly revision: number;
  readonly updatedAt: string;
  readonly deleted: boolean;
}

/** One bounded search page (deterministic keyset pagination). */
export interface AdminDoSearchPage {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly filters: Readonly<Record<string, string | null>>;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly rows: readonly AdminDoSearchRow[];
}

/** REQ-073/074 completion projection (server-authoritative). */
export interface AdminDoCompletionProjection {
  readonly state: string;
  readonly authoritative: boolean;
  readonly source: string;
  readonly deviceAuthoritative: boolean;
  readonly completedAt: string | null;
  readonly eventId: string | null;
  readonly validatedBy: string | null;
  readonly reasonCodes: readonly string[];
}

/** Read-only detail projection for one DO (REQ-044/054/073/074/076). */
export interface AdminDoDetail {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly deliveryOrder: {
    readonly id: string;
    readonly doNumber: string;
    readonly status: string;
    readonly driverRef: string | null;
    readonly vehicleRef: string | null;
    readonly customerRef: string | null;
    readonly revision: number;
    readonly updatedAt: string;
    readonly deleted: boolean;
  };
  readonly completion: AdminDoCompletionProjection;
  readonly checkpoints: {
    readonly total: number;
    readonly completed: number;
    readonly lastCheckpointId: string | null;
    readonly lastCompletedAt: string | null;
  };
  readonly audit: {
    readonly acceptedCompletionEvents: number;
    readonly lastAction: string | null;
    readonly lastActorType: string | null;
    readonly lastOccurredAt: string | null;
  };
}

/** Filters accepted by the admin DO search (all optional; server-validated). */
export interface AdminDoSearchQuery {
  readonly doNumber?: string;
  readonly status?: string;
  readonly from?: string;
  readonly to?: string;
  readonly driverRef?: string;
  readonly vehicleRef?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** Outcome of an admin DO search fetch (status + payload/message). */
export interface AdminDoSearchResult {
  readonly status: number;
  readonly payload: AdminDoSearchPage | null;
  readonly message: string | null;
}

/** Outcome of an admin DO detail fetch (status + payload/message). */
export interface AdminDoDetailResult {
  readonly status: number;
  readonly payload: AdminDoDetail | null;
  readonly message: string | null;
}

/** Geofence-required checkpoints (ODE-P00-T06 frozen applicability). */
export type GeofenceCheckpointId = 'CP3' | 'CP6';

/** The four non-override geofence decision states (DND §8) — an exception. */
export type GeofenceExceptionDecision =
  | 'OUTSIDE_GEOFENCE'
  | 'LOCATION_ACCURACY_INSUFFICIENT'
  | 'LOCATION_UNAVAILABLE'
  | 'VALIDATION_PENDING';

/** Consult-time override state (EXPIRED is projected, never persisted). */
export type GeofenceOverrideConsultState = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

/** One admin geofence exception row (REQ-049/067 read model). */
export interface AdminGeofenceExceptionRow {
  readonly exceptionId: string;
  readonly deliveryOrderId: string;
  readonly doNumber: string;
  readonly checkpointId: GeofenceCheckpointId;
  readonly decision: GeofenceExceptionDecision;
  readonly driverRef: string | null;
  readonly vehicleRef: string | null;
  readonly locationId: string | null;
  readonly distanceMeters: number | null;
  readonly accuracyMeters: number | null;
  readonly occurredAt: string;
  /** Consult-time override state for the (DO, checkpoint) pair; null = none. */
  readonly overrideState: GeofenceOverrideConsultState | null;
  readonly overridden: boolean;
}

/** One bounded exceptions page (deterministic keyset pagination). */
export interface AdminGeofenceExceptionPage {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly filters: Readonly<Record<string, string | null>>;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly rows: readonly AdminGeofenceExceptionRow[];
}

/** One admin geofence override row (consult-time state + provenance). */
export interface AdminGeofenceOverrideRow {
  readonly deliveryOrderId: string;
  readonly checkpointId: GeofenceCheckpointId;
  readonly state: GeofenceOverrideConsultState;
  readonly storedState: 'ACTIVE' | 'REVOKED';
  readonly reason: string;
  readonly actorType: string;
  readonly actorId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  readonly revokedBy: string | null;
  readonly revokeReason: string | null;
  readonly auditRef: string | null;
}

/** One bounded overrides page. */
export interface AdminGeofenceOverridePage {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly filters: Readonly<Record<string, string | null>>;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly rows: readonly AdminGeofenceOverrideRow[];
}

/** Read-only detail: an exception joined to its override provenance. */
export interface AdminGeofenceExceptionDetail {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly exception: AdminGeofenceExceptionRow;
  readonly override: AdminGeofenceOverrideRow | null;
}

/** Filters accepted by the exception list (all optional; server-validated). */
export interface AdminGeofenceExceptionsQuery {
  readonly deliveryOrderId?: string;
  readonly doNumber?: string;
  readonly checkpointId?: GeofenceCheckpointId;
  readonly driverRef?: string;
  readonly vehicleRef?: string;
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** Filters accepted by the override list. */
export interface AdminGeofenceOverridesQuery {
  readonly deliveryOrderId?: string;
  readonly checkpointId?: GeofenceCheckpointId;
  readonly state?: GeofenceOverrideConsultState;
  readonly limit?: number;
  readonly cursor?: string;
}

/** Payload for creating a supervisor override (ODE-P05-T04 write path). */
export interface GeofenceOverrideCreateInput {
  readonly deliveryOrderId: string;
  readonly checkpointId: GeofenceCheckpointId;
  readonly reason: string;
  readonly expiresAt?: string | null;
}

/** Outcome of an admin geofence exception/override read (status + payload/message). */
export interface AdminGeofenceReadResult<T> {
  readonly status: number;
  readonly payload: T | null;
  readonly message: string | null;
}

/** Outcome of an override create/revoke mutation (P05-T04 write path). */
export interface GeofenceOverrideMutationResult {
  readonly status: number;
  readonly outcome: string | null;
  readonly message: string | null;
}

export interface AdminUangJalanRow {
  readonly entityId: string;
  readonly doNumber: string | null;
  /** Server-authoritative OD-012 Uang Jalan status. */
  readonly status: string;
  /** REQ-011 gate state (SATISFIED / PENDING / BLOCKED). */
  readonly gate: string;
  readonly progressionAllowed: boolean;
  readonly amount: number | null;
  readonly currency: string | null;
  readonly version: number;
  readonly approvalRequirement: string | null;
  readonly updatedAt: string;
  readonly terminal: boolean;
  readonly reversalEligible: boolean;
  readonly reversed: boolean;
  readonly idempotencyEvents: number;
}

/** One bounded Uang Jalan page (deterministic keyset pagination). */
export interface AdminUangJalanPage {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly filters: Readonly<Record<string, string | boolean | null>>;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly rows: readonly AdminUangJalanRow[];
}

/** ODE-P06-T04 duplicate/reversal control state (observed, never performed). */
export interface AdminUangJalanControlState {
  readonly duplicateProtected: boolean;
  readonly idempotencyEvents: number;
  readonly replays: number;
  readonly deniedAttempts: number;
  readonly mutatedEvents: number;
  readonly reversalEligible: boolean;
  readonly alreadyReversed: boolean;
  readonly terminal: boolean;
  readonly reversalGuardCode: string | null;
}

/** Read-only detail projection for one Uang Jalan record. */
export interface AdminUangJalanDetail {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly record: {
    readonly entityId: string;
    readonly doNumber: string | null;
    readonly status: string;
    readonly amount: number | null;
    readonly currency: string | null;
    readonly version: number;
    readonly approvalRequirement: string | null;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly createdBy: { readonly role: string; readonly userId: string; readonly source: string };
    readonly updatedBy: { readonly role: string; readonly userId: string; readonly source: string };
  };
  readonly gate: {
    readonly state: string;
    readonly progressionAllowed: boolean;
    readonly note: string;
  };
  readonly controls: AdminUangJalanControlState;
  readonly audit: {
    readonly entries: number;
    readonly chainIntact: boolean;
    readonly chainErrors: readonly string[];
    readonly lastOperation: string | null;
    readonly lastOutcome: string | null;
    readonly lastActorRole: string | null;
    readonly lastActorId: string | null;
    readonly lastActorSource: string | null;
    readonly lastOccurredAt: string | null;
  };
  readonly auditTrail: readonly {
    readonly eventId: string;
    readonly sequence: number;
    readonly timestamp: string;
    readonly operation: string;
    readonly fromStatus: string | null;
    readonly toStatus: string | null;
    readonly outcome: string;
    readonly mutated: boolean;
    readonly replayed: boolean;
    readonly reason: string | null;
    readonly actorRole: string;
    readonly actorId: string;
    readonly actorSource: string;
    readonly idempotencyKey: string | null;
  }[];
}

/** Filters accepted by the admin Uang Jalan list (all optional; server-validated). */
export interface AdminUangJalanQuery {
  readonly doNumber?: string;
  readonly status?: string;
  readonly from?: string;
  readonly to?: string;
  readonly actorId?: string;
  readonly reversalEligible?: boolean;
  readonly reversed?: boolean;
  readonly limit?: number;
  readonly cursor?: string;
}

/** Outcome of an admin Uang Jalan list fetch (status + payload/message). */
export interface AdminUangJalanListResult {
  readonly status: number;
  readonly payload: AdminUangJalanPage | null;
  readonly message: string | null;
}

/** Outcome of an admin Uang Jalan detail fetch (status + payload/message). */
export interface AdminUangJalanDetailResult {
  readonly status: number;
  readonly payload: AdminUangJalanDetail | null;
  readonly message: string | null;
}

export interface JourneyHistorySummary {
  /** Journey id == tracking-session id (REQ-040 grouping key). */
  readonly journeyId: string;
  readonly deliveryOrderId: string;
  readonly driverId: string;
  readonly state: string;
  readonly startedAtEpochMs: number | null;
  readonly endedAtEpochMs: number | null;
  readonly sampleCount: number;
  readonly firstFixAtEpochMs: number | null;
  readonly lastFixAtEpochMs: number | null;
  /** Derived cumulative haversine distance (REQ-042), meters. */
  readonly distanceMeters: number;
  /** Derived stop-point count (REQ-041). */
  readonly stopPointCount: number;
}

/** One route polyline point (REQ-040 — server-ordered by sequence). */
export interface JourneyHistoryPolylinePoint {
  readonly sequence: number;
  readonly capturedAtEpochMs: number;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters: number | null;
}

/** One derived stop point (REQ-041 — dwell-time threshold, server-derived). */
export interface JourneyHistoryStopPoint {
  readonly stopId: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly arrivedAtEpochMs: number;
  readonly departedAtEpochMs: number;
  readonly dwellMs: number;
  readonly sampleCount: number;
}

/** One recorded tracking gap (coordinates are ALWAYS null — never fabricated). */
export interface JourneyHistoryGap {
  readonly fromEpochMs: number;
  readonly toEpochMs: number | null;
  readonly reason: string;
}

/** Journey detail (REQ-040/041/042 — full read surface for one journey). */
export interface JourneyHistoryDetail extends JourneyHistorySummary {
  /** Route polyline, ordered by sequence (bounded by the server). */
  readonly polyline: readonly JourneyHistoryPolylinePoint[];
  /** Derived stop points (REQ-041). */
  readonly stopPoints: readonly JourneyHistoryStopPoint[];
  /** Recorded gaps (never fabricated coordinates). */
  readonly gaps: readonly JourneyHistoryGap[];
}

/** Bounded journey list result (page + total + hasMore) — REQ-046. */
export interface JourneyHistoryListResult {
  readonly journeys: readonly JourneyHistorySummary[];
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/** Filters accepted by the admin journey list (bounded; server-validated). */
export interface JourneyHistoryListQuery {
  /** 1-based page number. */
  readonly page: number;
  /** Page size, 1..100. */
  readonly limit: number;
  readonly driverId?: string;
  readonly journeyId?: string;
  /** Inclusive start of the journey-start window (epoch ms). */
  readonly from?: number;
  /** Inclusive end of the journey-start window (epoch ms). */
  readonly to?: number;
}

/** Outcome of a journey list fetch (status + payload/message). */
export interface JourneyHistoryListFetchResult {
  readonly status: number;
  readonly payload: JourneyHistoryListResult | null;
  readonly message: string | null;
}

/** Outcome of a journey detail fetch (status + payload/message). */
export interface JourneyHistoryDetailFetchResult {
  readonly status: number;
  readonly payload: JourneyHistoryDetail | null;
  readonly message: string | null;
}

export interface CheckpointTimelineGps {
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters: number | null;
}

/** One CP1..CP7 timeline slot (REQ-012/031/045/076) — server-derived. */
export interface CheckpointTimelineSlot {
  readonly checkpoint: string;
  readonly name: string;
  readonly order: number;
  readonly state: 'completed' | 'locked' | 'pending';
  readonly lifecycleState: string | null;
  readonly authoritative: boolean;
  readonly deviceAuthoritative: boolean;
  readonly source: string;
  readonly recordSource: string | null;
  readonly occurredAt: string | null;
  readonly gps: CheckpointTimelineGps | null;
  readonly actor: { readonly type: string; readonly id: string } | null;
  readonly executionSource: string | null;
  readonly auditEventId: string | null;
  readonly reason: string | null;
  readonly blockingReason: string | null;
  readonly blockingCheckpoint: string | null;
}

/** The admin checkpoint timeline for one delivery order (REQ-045). */
export interface CheckpointTimeline {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly deliveryOrderId: string;
  readonly doNumber: string | null;
  readonly doStatus: string | null;
  readonly checkpoints: readonly CheckpointTimelineSlot[];
  readonly summary: {
    readonly total: number;
    readonly completed: number;
    readonly locked: number;
    readonly pending: number;
    readonly lastCompletedCheckpointId: string | null;
    readonly lastCompletedAt: string | null;
  };
  readonly source: string;
  readonly deviceAuthoritative: boolean;
  readonly audit: {
    readonly events: number;
    readonly acceptedTransitions: number;
    readonly deniedTransitions: number;
    readonly lastAction: string | null;
    readonly lastActorType: string | null;
    readonly lastOccurredAt: string | null;
  };
}

/** Outcome of an admin checkpoint-timeline fetch (status + payload/message). */
export interface CheckpointTimelineResult {
  readonly status: number;
  readonly payload: CheckpointTimeline | null;
  readonly message: string | null;
}

/** One operational report row (REQ-048) — opaque operational ids only (REQ-078). */
export type ReportRow = Readonly<Record<string, unknown>>;

/** Aggregate over the full filtered report set (server-derived, REQ-048). */
export interface ReportsTotals {
  readonly doCount: number;
  readonly journeyCount: number;
  readonly distanceMeters: number;
  readonly stopPointCount: number;
  readonly checkpointsCompleted: number;
  readonly uangJalanDisbursedCount: number;
  readonly amountsByCurrency: Readonly<Record<string, number>>;
}

/** One bounded, deterministically ordered operational report page (REQ-048). */
export interface ReportsPage {
  readonly schemaVersion: number;
  readonly kind: string;
  readonly range: { readonly from: string | null; readonly to: string | null };
  readonly filters: {
    readonly driverId: string | null;
    readonly vehicleId: string | null;
    readonly deliveryOrderId: string | null;
  };
  readonly sort: string;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly rows: readonly ReportRow[];
  readonly totals: ReportsTotals;
}

/** Bounded report query (only validated values are ever sent). */
export interface ReportsQuery {
  readonly kind: string;
  readonly from?: string;
  readonly to?: string;
  readonly driverId?: string;
  readonly vehicleId?: string;
  readonly doId?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** Durable evidence acknowledgement state (DND §6 / ODE-P01-T06). */
export type EvidenceAckState = 'PENDING_UPLOAD' | 'SERVER_ACKNOWLEDGED';

/** One admin evidence row (REQ-047) — metadata only, never a storage key. */
export interface AdminEvidenceRow {
  readonly evidenceId: string;
  readonly deliveryOrderId: string;
  readonly checkpointId: string;
  readonly evidenceType: string;
  readonly localId: string;
  readonly ackState: EvidenceAckState;
  readonly retentionUntilAck: boolean;
  readonly serverEvidenceId: string | null;
  readonly checksum: string | null;
  readonly sizeBytes: number | null;
  readonly contentType: string | null;
  readonly capturedAt: string | null;
  readonly acknowledgedAt: string | null;
  readonly acknowledgedBy: string | null;
  readonly locationRef: {
    readonly latitude: number;
    readonly longitude: number;
    readonly accuracyMeters: number | null;
  } | null;
  readonly objectKeySha256: string;
}

/** One bounded page of evidence metadata (cursor pagination). */
export interface AdminEvidencePage {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly filters: {
    readonly deliveryOrderId: string | null;
    readonly checkpointId: string | null;
    readonly evidenceType: string | null;
    readonly ackState: EvidenceAckState | null;
  };
  readonly limit: number;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly summary: {
    readonly total: number;
    readonly acknowledged: number;
    readonly pending: number;
  };
  readonly rows: readonly AdminEvidenceRow[];
  readonly source: string;
  readonly deviceAuthoritative: boolean;
}

/** Short-lived presigned media reference (REQ-078). */
export interface AdminEvidenceMedia {
  readonly url: string;
  readonly expiresAt: string;
  readonly expiresSeconds: number;
  readonly objectKeySha256: string;
}

/** Read-only detail projection for one evidence item (REQ-047/063/078). */
export interface AdminEvidenceDetail {
  readonly schemaVersion: number;
  readonly asOfEpochMs: number;
  readonly evidence: AdminEvidenceRow;
  readonly linkage: {
    readonly ackMarkerPresent: boolean;
    readonly ackMarkerKeySha256: string;
    readonly serverEvidenceId: string | null;
    readonly acknowledgedBy: string | null;
  };
  readonly media: AdminEvidenceMedia | null;
  readonly mediaState: 'AVAILABLE' | 'MISSING';
  readonly mediaTtlSeconds: number;
  readonly source: string;
  readonly deviceAuthoritative: boolean;
}

/** Filters accepted by the admin evidence list (all optional). */
export interface AdminEvidenceQuery {
  readonly deliveryOrderId?: string;
  readonly checkpointId?: string;
  readonly evidenceType?: string;
  readonly ackState?: EvidenceAckState | '';
  readonly limit?: number;
  readonly cursor?: string;
}

/** Outcome of a report read fetch (status + payload/message). */
export interface ReportsFetchResult {
  readonly status: number;
  readonly payload: ReportsPage | null;
  readonly message: string | null;
}

/**
 * Outcome of a stream-only export fetch (REQ-048/088). On success `body` holds
 * the exact server bytes (never re-derived client-side) together with the
 * server-reported content type, row count and REQ-076 output hash. On failure
 * `errorCode` carries the OD-015 error code so the caller can distinguish the
 * REQ-088 `EXPORT_NOT_AUTHORIZED` gate from a role/purpose denial.
 */
export interface ReportExportResult {
  readonly status: number;
  readonly body: string | null;
  readonly contentType: string | null;
  readonly outputHash: string | null;
  readonly rowCount: number | null;
  readonly errorCode: string | null;
  readonly message: string | null;
}

/** One export request (format + purpose code + the bounded report query). */
export interface ReportExportRequest {
  readonly query: ReportsQuery;
  readonly format: 'csv' | 'json';
  readonly purposeCode: string;
}


/** Outcome of an admin evidence list fetch (status + payload/message). */
export interface AdminEvidenceListResult {
  readonly status: number;
  readonly payload: AdminEvidencePage | null;
  readonly message: string | null;
}

/** Outcome of an admin evidence detail fetch (status + payload/message). */
export interface AdminEvidenceDetailResult {
  readonly status: number;
  readonly payload: AdminEvidenceDetail | null;
  readonly message: string | null;
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    // NOTE: never default to the bare `fetch` reference — calling it as a method of this
    // instance makes the browser reject it with "Illegal invocation" before any network
    // I/O (see api/client.fetch-bind.test.ts). Bind it to the global receiver instead.
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  private async request(path: string, init: RequestInit): Promise<Response> {
    return this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    });
  }

  /** Staff login. Throws `ApiRequestError` on 401/503/other non-2xx. */
  async login(staffId: string, credential: string): Promise<LoginResult> {
    const response = await this.request('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ staffId, credential }),
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as LoginResult;
  }

  /**
   * Fetches a guarded dashboard module. Never throws for HTTP outcomes —
   * returns `{ status, payload, message }` so the caller can map to REQ-090
   * states (200 → empty, 401 → unauthorized, 403 → blocked, 503 → error).
   */
  async fetchModule(
    modulePath: string,
    token: string,
    purposeCode?: string,
  ): Promise<ModuleFetchResult> {
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (purposeCode !== undefined && purposeCode.trim() !== '') {
      headers['x-purpose-code'] = purposeCode.trim();
    }
    const response = await this.request(`/admin/dashboard/${modulePath}`, {
      method: 'GET',
      headers,
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: body,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body, message: null };
  }

  /**
   * Fetches the server-derived operational overview snapshot (REQ-043/044/048).
   * Never throws for HTTP outcomes — returns `{ status, payload, message }` so
   * the caller can map to REQ-090 states (200 → ready/empty, 401 →
   * unauthorized, 403 → blocked, 503 → error).
   */
  async fetchOverviewSnapshot(token: string): Promise<OverviewFetchResult> {
    const response = await this.request('/admin/overview/snapshot', {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as OverviewSnapshot, message: null };
  }

  /**
   * WebSocket URL for the live overview feed (REQ-043/044). Browser
   * WebSocket clients cannot set an `Authorization` header, so the staff
   * token travels as the `access_token` query parameter and the server
   * normalizes it into the shared staff guard before authorizing.
   */
  overviewStreamUrl(token: string): string {
    const path = `/admin/overview/stream?access_token=${encodeURIComponent(token)}`;
    const base = `${this.baseUrl}${path}`;
    if (/^https?:\/\//.test(this.baseUrl)) {
      return base.replace(/^http/, 'ws');
    }
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    return `${origin}${base}`.replace(/^http/, 'ws');
  }

  /**
   * Fetches the admin DO search page (REQ-044 + REQ-074 states) with the
   * bounded filters and the opaque keyset cursor. Never throws for HTTP
   * outcomes — returns `{ status, payload, message }` so the caller can map
   * to REQ-090 states (200 → ready/empty, 400 → validation error, 401 →
   * unauthorized, 403 → blocked, 503 → error).
   */
  async searchDeliveryOrders(
    query: AdminDoSearchQuery,
    token: string,
  ): Promise<AdminDoSearchResult> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const suffix = params.toString();
    const response = await this.request(`/admin/do-search${suffix === '' ? '' : `?${suffix}`}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminDoSearchPage, message: null };
  }

  /**
   * Fetches the read-only detail projection for one DO. Never throws for HTTP
   * outcomes (200 → ready, 404 → not found, 401/403/503 → the matching
   * REQ-090 state).
   */
  async fetchDeliveryOrderDetail(doId: string, token: string): Promise<AdminDoDetailResult> {
    const response = await this.request(`/admin/do-search/${encodeURIComponent(doId)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminDoDetail, message: null };
  }

  /**
   * Fetches the admin geofence exception page (REQ-049/067) with the bounded
   * filters and the opaque keyset cursor. Never throws for HTTP outcomes —
   * returns `{ status, payload, message }` so the caller can map to REQ-090
   * states (200 → ready/empty, 400 → validation error, 401 → unauthorized,
   * 403 → blocked, 503 → error).
   */
  async listGeofenceExceptions(
    query: AdminGeofenceExceptionsQuery,
    token: string,
  ): Promise<AdminGeofenceReadResult<AdminGeofenceExceptionPage>> {
    return this.getPage<AdminGeofenceExceptionPage>('/admin/geofence-exceptions', query, token);
  }

  /** Fetches the admin geofence override page with consult-time states. */
  async listGeofenceOverrides(
    query: AdminGeofenceOverridesQuery,
    token: string,
  ): Promise<AdminGeofenceReadResult<AdminGeofenceOverridePage>> {
    return this.getPage<AdminGeofenceOverridePage>(
      '/admin/geofence-exceptions/overrides',
      query,
      token,
    );
  }

  /** Fetches the read-only exception detail (joined override provenance). */
  async fetchGeofenceExceptionDetail(
    exceptionId: string,
    token: string,
  ): Promise<AdminGeofenceReadResult<AdminGeofenceExceptionDetail>> {
    const response = await this.request(
      `/admin/geofence-exceptions/${encodeURIComponent(exceptionId)}`,
      { method: 'GET', headers: { authorization: `Bearer ${token}` } },
    );
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminGeofenceExceptionDetail, message: null };
  }

  /**
   * Creates a supervisor geofence override via the VERIFIED ODE-P05-T04 write
   * route (supervisor-only `geofence.override`, mandatory `x-purpose-code`).
   * This client never writes the override store directly (REQ-067/077).
   */
  async createGeofenceOverride(
    input: GeofenceOverrideCreateInput,
    token: string,
    purposeCode: string,
  ): Promise<GeofenceOverrideMutationResult> {
    const response = await this.request('/geofence-overrides', {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'x-purpose-code': purposeCode },
      body: JSON.stringify(input),
    });
    return this.toMutationResult(response);
  }

  /**
   * Revokes a supervisor geofence override via the VERIFIED ODE-P05-T04 revoke
   * route (atomic ACTIVE → REVOKED; idempotent no-op when already revoked).
   */
  async revokeGeofenceOverride(
    deliveryOrderId: string,
    checkpointId: GeofenceCheckpointId,
    reason: string,
    token: string,
    purposeCode: string,
  ): Promise<GeofenceOverrideMutationResult> {
    const response = await this.request(
      `/geofence-overrides/${encodeURIComponent(deliveryOrderId)}/checkpoints/${checkpointId}/revoke`,
      {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'x-purpose-code': purposeCode },
        body: JSON.stringify({ reason }),
      },
    );
    return this.toMutationResult(response);
  }

  private async getPage<T>(
    path: string,
    query: object,
    token: string,
  ): Promise<AdminGeofenceReadResult<T>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const suffix = params.toString();
    const response = await this.request(`${path}${suffix === '' ? '' : `?${suffix}`}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as T, message: null };
  }

  private async toMutationResult(response: Response): Promise<GeofenceOverrideMutationResult> {
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        outcome: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    const data = (body as { data?: { outcome?: unknown } } | null)?.data;
    return {
      status: response.status,
      outcome: typeof data?.outcome === 'string' ? data.outcome : null,
      message: null,
    };
  }

  /**
   * Fetches the admin Uang Jalan list page (REQ-011 gate + ODE-P06-T04
   * control state) with the bounded filters and the opaque keyset cursor.
   * Never throws for HTTP outcomes — returns `{ status, payload, message }` so
   * the caller can map to REQ-090 states (200 → ready/empty, 400 → validation
   * error, 401 → unauthorized, 403 → blocked, 503 → error).
   */
  async listUangJalan(
    query: AdminUangJalanQuery,
    token: string,
  ): Promise<AdminUangJalanListResult> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const suffix = params.toString();
    const response = await this.request(`/admin/uang-jalan${suffix === '' ? '' : `?${suffix}`}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminUangJalanPage, message: null };
  }

  /**
   * Fetches the read-only detail projection for one Uang Jalan record. Never
   * throws for HTTP outcomes (200 → ready, 404 → not found, 401/403/503 → the
   * matching REQ-090 state).
   */
  async fetchUangJalanDetail(
    entityId: string,
    token: string,
  ): Promise<AdminUangJalanDetailResult> {
    const response = await this.request(`/admin/uang-jalan/${encodeURIComponent(entityId)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminUangJalanDetail, message: null };
  }

  /**
   * Fetches the admin journey-history page (REQ-046 list: summaries only,
   * bounded pagination + driver/date/journey filters). Never throws for HTTP
   * outcomes — returns `{ status, payload, message }` so the caller can map
   * to REQ-090 states (200 → ready/empty, 400 → validation error, 401 →
   * unauthorized, 403 → blocked, 503 → error).
   */
  async listJourneyHistory(
    query: JourneyHistoryListQuery,
    token: string,
  ): Promise<JourneyHistoryListFetchResult> {
    const params = new URLSearchParams();
    params.set('page', String(query.page));
    params.set('limit', String(query.limit));
    if (query.driverId !== undefined && query.driverId !== '') params.set('driverId', query.driverId);
    if (query.journeyId !== undefined && query.journeyId !== '') {
      params.set('journeyId', query.journeyId);
    }
    if (query.from !== undefined) params.set('from', String(query.from));
    if (query.to !== undefined) params.set('to', String(query.to));
    const response = await this.request(`/admin/journey-history?${params.toString()}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as JourneyHistoryListResult, message: null };
  }

  /**
   * Fetches the read-only journey detail (REQ-040 polyline + REQ-041 stop
   * points + REQ-042 derived distance). The server wraps the projection in
   * `{ journey }`; a 200 without that wrapper yields a null payload so the
   * caller fails closed instead of rendering a fabricated journey. Never
   * throws for HTTP outcomes (200 → ready, 404 → not found, 401/403/503 →
   * the matching REQ-090 state).
   */
  async fetchJourneyHistoryDetail(
    journeyId: string,
    token: string,
  ): Promise<JourneyHistoryDetailFetchResult> {
    const response = await this.request(`/admin/journey-history/${encodeURIComponent(journeyId)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    const wrapper = body as { journey?: unknown } | null;
    const journey = wrapper === null ? undefined : wrapper.journey;
    if (journey === undefined || journey === null || typeof journey !== 'object') {
      return { status: response.status, payload: null, message: 'Respons detail perjalanan tidak valid.' };
    }
    return {
      status: response.status,
      payload: journey as JourneyHistoryDetail,
      message: null,
    };
  }

  /**
   * Fetches the admin checkpoint timeline for one delivery order (REQ-045:
   * CP1..CP7 with the REQ-012 completed/locked/pending states, the REQ-031
   * captured GPS + server timestamp and the REQ-076 actor/audit linkage).
   * Never throws for HTTP outcomes — returns `{ status, payload, message }` so
   * the caller can map to REQ-090 states (200 → ready/empty, 400 → validation
   * error, 401 → unauthorized, 403 → blocked, 404 → not found, 503 → error).
   */
  async fetchCheckpointTimeline(
    doId: string,
    token: string,
  ): Promise<CheckpointTimelineResult> {
    const response = await this.request(`/admin/checkpoint-timeline/${encodeURIComponent(doId)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as CheckpointTimeline, message: null };
  }

  /**
   * Fetches one bounded operational report page (REQ-048: fixed report kinds,
   * explicit filters, deterministic sort, opaque keyset cursor). Never throws
   * for HTTP outcomes — returns `{ status, payload, message }` so the caller
   * can map to REQ-090 states (200 → ready/empty, 400 → validation error,
   * 401 → unauthorized, 403 → blocked, 503 → error).
   */
  async fetchReports(query: ReportsQuery, token: string): Promise<ReportsFetchResult> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const response = await this.request(`/admin/reports?${params.toString()}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as ReportsPage, message: null };
  }

  /**
   * Fetches the stream-only report export (REQ-048/088). The export is
   * fail-closed behind the owner-approved policy: an unapproved deployment
   * answers 403 `EXPORT_NOT_AUTHORIZED` with zero bytes (surfaced as
   * `errorCode`). The returned `body` is the server's exact serialized export
   * — the client never re-serializes, re-computes or truncates it.
   */
  async fetchReportExport(
    request: ReportExportRequest,
    token: string,
  ): Promise<ReportExportResult> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(request.query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    params.set('format', request.format);
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (request.purposeCode.trim() !== '') {
      headers['x-purpose-code'] = request.purposeCode.trim();
    }
    const response = await this.request(`/admin/reports/export?${params.toString()}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as ApiErrorEnvelope | null;
      return {
        status: response.status,
        body: null,
        contentType: null,
        outputHash: null,
        rowCount: null,
        errorCode: envelope?.error?.code ?? null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    const text = await response.text();
    const rowCountHeader = response.headers.get('x-report-row-count');
    return {
      status: response.status,
      body: text,
      contentType: response.headers.get('content-type'),
      outputHash: response.headers.get('x-report-output-hash'),
      rowCount: rowCountHeader === null ? null : Number(rowCountHeader),
      errorCode: null,
      message: null,
    };
  }

  /**
   * Lists the admin evidence history (REQ-047) with the bounded filters and
   * the opaque keyset cursor. Never throws for HTTP outcomes — returns
   * `{ status, payload, message }` so the caller can map to REQ-090 states
   * (200 → ready/empty, 400 → validation error, 401 → unauthorized, 403 →
   * blocked, 503 → error).
   */
  async listEvidence(
    query: AdminEvidenceQuery,
    token: string,
  ): Promise<AdminEvidenceListResult> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const suffix = params.toString();
    const response = await this.request(`/admin/evidence${suffix === '' ? '' : `?${suffix}`}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminEvidencePage, message: null };
  }

  /**
   * Fetches the read-only detail projection for one evidence item, including
   * the short-lived (<= 300 s) presigned media reference (REQ-078). Never
   * throws for HTTP outcomes (200 → ready, 400 → malformed reference,
   * 404 → not found, 401/403/503 → the matching REQ-090 state).
   */
  async fetchEvidenceDetail(
    evidenceId: string,
    token: string,
  ): Promise<AdminEvidenceDetailResult> {
    const response = await this.request(`/admin/evidence/${encodeURIComponent(evidenceId)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = body as ApiErrorEnvelope | null;
      return {
        status: response.status,
        payload: null,
        message: envelope?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return { status: response.status, payload: body as AdminEvidenceDetail, message: null };
  }

  private async toApiError(response: Response): Promise<ApiRequestError> {
    const body = (await response.json().catch(() => null)) as ApiErrorEnvelope | null;
    return new ApiRequestError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `HTTP ${response.status}`,
      body?.requestId ?? null,
    );
  }
}

export const api = new ApiClient(import.meta.env.VITE_API_BASE_URL ?? '/v1');
